import camelize from 'camelize';
// @ts-ignore TODO: Add types for decamelize-keys
import decamelizeKeys from 'decamelize-keys';
import { baseCreateApi } from 'services';
import {
  AvailablePlansResponse,
  CalculateCostResponse,
  CheckoutResponse,
  ICancelSubscriptionPayload,
  IScheduledPromptCreditUsageResponse,
  ISubscriptionCreditPurchase,
  SubscriptionCreditBalanceResponse,
  SubscriptionCreditPricingResponse,
  SubscriptionCreditUsageByUserResponse,
  SubscriptionResponse
} from 'types/subscription';

import Licenses from 'helpers/Licenses';
import { stripPlanPeriod, strippedPlanName } from 'helpers/formatting';

import { BILLING_ADDONS, PLAN_NAMES } from 'constants/billing';

interface GetAvailablePlansArgs {
  showLegacyPlans?: boolean;
}

type FeatureDataResponse = {
  [feature: string]: {
    planIds: string[];
    addonIds: string[];
    limits?: {
      limitName: string;
      limitPerPlan?: { [key: string]: number };
      limitPerAddon?: { [key: string]: number };
    };
  };
};

// Helper functions to reduce cognitive complexity
const processLimitUpgrade = (limits: any) => {
  const { limit_per_addon: limitPerAddon, limit_per_plan: limitPerPlan } =
    limits;

  if (limitPerAddon && Object.keys(limitPerAddon).length > 0) {
    const firstAddonKey = Object.keys(limitPerAddon)[0];
    const baseAddonId = stripPlanPeriod(firstAddonKey);
    return (
      `${BILLING_ADDONS[baseAddonId as keyof typeof BILLING_ADDONS]} Addon` ??
      ''
    );
  }

  if (limitPerPlan && Object.keys(limitPerPlan).length > 0) {
    const firstPlanKey = Object.keys(limitPerPlan)[0];
    const basePlanId = stripPlanPeriod(firstPlanKey);
    return `${PLAN_NAMES[basePlanId as keyof typeof PLAN_NAMES]} Plan` ?? '';
  }

  return '';
};

const processAddons = (addonIds: string[]) => {
  const uniqueAddons = new Set(
    addonIds
      .map(addonId => {
        const baseAddonId = stripPlanPeriod(addonId);
        return BILLING_ADDONS[baseAddonId as keyof typeof BILLING_ADDONS] ?? '';
      })
      .filter(Boolean)
  );

  const addons = Array.from(uniqueAddons).join(' / ');
  return `${addons} ${uniqueAddons.size > 1 ? 'Addons' : 'Addon'}`;
};

const processPlans = (planIds: string[]) => {
  const lowestPlan = planIds[planIds.length - 1];
  const basePlanId = lowestPlan.replace(
    /-(?:monthly|yearly|half-yearly|quarterly)$/,
    ''
  );

  if (PLAN_NAMES[basePlanId as keyof typeof PLAN_NAMES]) {
    return `${PLAN_NAMES[basePlanId as keyof typeof PLAN_NAMES]} Plan`;
  }

  const strippedPlan = strippedPlanName(lowestPlan.replace(/-/g, ' '));
  const matchingPlan = Object.entries(PLAN_NAMES).find(
    ([key]) => strippedPlanName(key.replace(/-/g, ' ')) === strippedPlan
  );

  return matchingPlan ? `${matchingPlan[1]} plan` : '';
};

export const subscriptionApi = baseCreateApi.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    getFeaturePlans: builder.query<
      string | FeatureDataResponse,
      {
        feature: string;
        requireLimitUpgrade?: boolean;
        requireEntireData?: boolean;
      }
    >({
      providesTags: ['FeaturePlans'],
      query: ({ feature }) => ({
        url: `/api/v2/subscriptions/feature_plans/?feature_name=${feature}`,
        method: 'GET'
      }),
      transformResponse: (
        response: {
          [feature: string]: {
            plan_ids: string[];
            addon_ids: string[];
            limits?: {
              limit_name: string;
              limit_per_plan?: { [key: string]: number };
              limit_per_addon?: { [key: string]: number };
            };
          };
        },
        _meta,
        args
      ): string | FeatureDataResponse => {
        if (args.requireEntireData) {
          return camelize(response);
        }

        if (!response) return '';

        const featureData = response[args.feature];
        if (!featureData) return '';

        if (args.requireLimitUpgrade && featureData.limits) {
          return processLimitUpgrade(featureData.limits);
        }

        if (featureData.addon_ids?.length > 0) {
          return processAddons(featureData.addon_ids);
        }

        if (featureData.plan_ids?.length > 0) {
          return processPlans(featureData.plan_ids);
        }

        return '';
      }
    }),
    getAvailablePlans: builder.query<
      AvailablePlansResponse[],
      GetAvailablePlansArgs
    >({
      providesTags: ['AvailablePlans'],
      query: () => ({
        url: '/api/v1/subscriptions/plans/',
        method: 'GET'
      }),
      transformResponse: (
        response: unknown,
        meta: {},
        args: GetAvailablePlansArgs = { showLegacyPlans: false }
      ) => {
        const plans = camelize(response) as AvailablePlansResponse[];
        const showLegacyPlans = args.showLegacyPlans ?? false;

        return plans.filter(plan =>
          Licenses[showLegacyPlans ? 'LEGACY_PLANS' : 'NEW_PLANS'].includes(
            plan.planId
          )
        );
      }
    }),
    getActiveSubscriptions: builder.query<SubscriptionResponse[], void>({
      providesTags: ['ActiveSubscriptions'],
      query: () => ({
        url: '/api/v2/subscriptions/',
        method: 'GET'
      }),
      transformResponse: (response: unknown) => {
        const camelizedResponse = camelize(response) as SubscriptionResponse[];
        return camelizedResponse
          .filter(subscription => !['cancelled'].includes(subscription.status))
          .map(subscription => {
            // Preserve the original reason_codes & scheduled_changes without camelizing
            const originalResponse = response as any[];
            const originalSubscription = originalResponse.find(
              s => s.subscription_id === subscription.subscriptionId
            );
            return {
              ...subscription,
              reasonCodes: originalSubscription?.reason_codes || {},
              scheduledChanges: originalSubscription?.scheduled_changes || {}
            };
          });
      }
    }),
    activateAddon: builder.mutation({
      query: (payload: {
        addonUuids: string[];
        subscriptionUuid: string;
        userEmail: string;
      }) => ({
        url: '/api/v1/licenses/addons/activate/',
        method: 'POST',
        data: decamelizeKeys(payload)
      })
    }),
    deactivateAddon: builder.mutation({
      query: (payload: {
        addonUuids: string[];
        subscriptionUuid: string;
        userEmail: string;
      }) => ({
        url: '/api/v1/licenses/addons/deactivate/',
        method: 'POST',
        data: decamelizeKeys(payload)
      })
    }),
    calculateCost: builder.mutation({
      query: payload => ({
        url: '/api/v2/subscriptions/calculate/',
        method: 'POST',
        data: decamelizeKeys(payload)
      }),
      transformResponse: (response: unknown) =>
        camelize(response) as CalculateCostResponse
    }),
    checkout: builder.mutation({
      query: payload => ({
        url: '/api/v2/subscriptions/checkout/',
        method: 'POST',
        data: decamelizeKeys(payload)
      }),
      transformResponse: (response: unknown) =>
        camelize(response) as CheckoutResponse
    }),
    cancelSubscription: builder.mutation({
      query: (payload: ICancelSubscriptionPayload) => ({
        url: '/api/v1/subscriptions/cancel_subscription/',
        method: 'POST',
        data: payload
      })
    }),
    getCreditBalance: builder.query<SubscriptionCreditBalanceResponse, void>({
      providesTags: ['BillingTopUpCredits'],
      query: () => ({
        url: '/api/v1/subscriptions/credits/balance/',
        method: 'GET'
      }),
      transformResponse: response =>
        camelize(response) as SubscriptionCreditBalanceResponse
    }),
    getCreditPricing: builder.query<SubscriptionCreditPricingResponse[], void>({
      query: () => ({
        url: '/api/v1/subscriptions/credit-pricing/',
        method: 'GET'
      }),
      transformResponse: response =>
        camelize(response) as SubscriptionCreditPricingResponse[]
    }),
    getCreditPurchases: builder.query<ISubscriptionCreditPurchase[], void>({
      query: () => ({
        url: '/api/v1/subscriptions/credits/purchases/',
        method: 'GET'
      }),
      transformResponse: response =>
        camelize(response) as ISubscriptionCreditPurchase[]
    }),
    getScheduledPromptCreditUsage: builder.query<
      IScheduledPromptCreditUsageResponse,
      { scheduledPromptUuid: string }
    >({
      query: ({ scheduledPromptUuid }) => ({
        url: `/api/v1/subscriptions/credits/usage/scheduled-prompts/${scheduledPromptUuid}/`,
        method: 'GET'
      }),
      transformResponse: response =>
        camelize(response) as IScheduledPromptCreditUsageResponse
    }),
    getCreditUsageByUsers: builder.query<
      SubscriptionCreditUsageByUserResponse[],
      { from: string; to: string }
    >({
      query: ({ from, to }) => ({
        url: `/api/v1/subscriptions/credits/usage/users/?from=${encodeURIComponent(
          from
        )}&to=${encodeURIComponent(to)}`,
        method: 'GET'
      }),
      transformResponse: response =>
        camelize(response) as SubscriptionCreditUsageByUserResponse[]
    }),
    getSubscriptionMigrationPlans: builder.query({
      query: () => ({
        url: '/api/v2/subscriptions/migration_plan/',
        method: 'GET',
        options: { ignoreErrorNotification: true }
      }),
      transformErrorResponse: (response: {
        status: number;
        data: {
          details: string;
          domain: string;
          error: string;
        };
      }) => {
        if (response.status === 412) {
          return response.data;
        }
        return { error: response.data };
      }
    })
  })
});

export const {
  useGetFeaturePlansQuery,
  useGetActiveSubscriptionsQuery,
  useGetAvailablePlansQuery,
  useActivateAddonMutation,
  useDeactivateAddonMutation,
  useCalculateCostMutation,
  useCheckoutMutation,
  useCancelSubscriptionMutation,
  useGetCreditBalanceQuery,
  useGetCreditPurchasesQuery,
  useGetCreditPricingQuery,
  useGetScheduledPromptCreditUsageQuery,
  useGetCreditUsageByUsersQuery,
  useGetSubscriptionMigrationPlansQuery
} = subscriptionApi;
